import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { debounce } from "lodash";
import {
  Progress,
  Button,
  Table,
  Breadcrumb,
  Input,
  Tooltip,
  Space,
  Modal,
  message,
  Popconfirm,
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import "../../../styles/Main.scss";
import { useTranslation } from "react-i18next";
import { getRequest, deleteRequest } from "../../../axios";
import { authUserData } from "../../../utils/Helper";
import {
  ShowErrorMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";

const DraftGoodsReceipt = ({
  embedded,
  refreshKey,
  onAddReceipt,
  onEditDraft,
} = {}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const CompanyId = authUserData()?.company || null;
  const { Search } = Input;
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [state, setState] = useState({
    page: 1,
    page_size: 10,
    search: "",
  });

  const fetchDraftGoodsReceipts = async () => {
    try {
      setLoading(true);
      const params = { page: state.page, page_size: state.page_size };
      if (state.search?.trim()) params.search = state.search.trim();
      const res = await getRequest("inventory-drafts/", params);
      const payload = res?.data;
      const count = payload?.count ?? payload?.results?.count ?? 0;
      setTotalCount(count);
      const results = payload?.results;
      const data = results?.data ?? results ?? [];
      const draftsList = Array.isArray(data) ? data : [];

      if (draftsList.length > 0) {
        const mappedData = draftsList.map((draft, index) => {
          const lineItems =
            draft?.items || draft?.line_items || draft?.data?.line_items || [];
          const confidenceScores = lineItems
            .map((item) => item?.confidence_score)
            .filter((s) => s !== undefined && s !== null);
          const avgConfidence =
            confidenceScores.length > 0
              ? Math.round(
                  (confidenceScores.reduce((sum, s) => sum + s, 0) /
                    confidenceScores.length) *
                    100,
                )
              : draft?.confidence_score != null
                ? Math.round(Number(draft.confidence_score) * 100)
                : 0;
                
          return {
            key: draft?.id ?? index + 1,
            id: draft?.id,
            invoice_pk: draft?.invoice_pk ?? draft?.id,
            sourcedocument: draft?.invoice_number || draft?.invoice_id || "N/A",
            vendor:
              draft?.vendor_details?.name ||
              draft?.vendor_name ||
              draft?.supplier ||
              "—",
            site: draft?.site_name || "—",
            extracted_date: draft?.extracted_date || "N/A",
            confidence: avgConfidence,
            status: draft?.status || "Draft",
            document_file_url: draft?.document_file_url ?? "",
          };
        });
        setTableData(mappedData);
      } else {
        setTableData([]);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      ShowErrorMsg(err);
      setTableData([]);
      setTotalCount(0);
    }
  };

  const handleTableChange = (pagination) => {
    setState((prev) => ({
      ...prev,
      page: pagination.current,
      page_size: pagination.pageSize,
    }));
  };

  useEffect(() => {
    fetchDraftGoodsReceipts();
  }, [state]);

  const prevRefreshKeyRef = useRef(undefined);
  useEffect(() => {
    if (!embedded || refreshKey == null) return;
    const prev = prevRefreshKeyRef.current;
    prevRefreshKeyRef.current = refreshKey;
    if (prev === undefined) return;
    fetchDraftGoodsReceipts();
  }, [embedded, refreshKey]);

  const searchCallbackDelayed = (e) => {
    const value = e?.target?.value ?? "";
    debouncedSearchRef(value);
  };
  const onSearch = (value) => {
    setState((prev) => ({ ...prev, search: value ?? "", page: 1 }));
  };
  const debouncedSearchRef = useRef(
    debounce((value) => onSearch(value), 500),
  ).current;

  const handleDelete = async (record) => {
    const invoicePk = record.invoice_pk ?? record.id;
    if (!invoicePk) {
      ShowErrorMsg("Invoice ID not found");
      return;
    }
    try {
      const res = await deleteRequest(`inventory-invoices/${invoicePk}/`);
      const msg =
        res?.data?.message ||
        res?.data?.results?.message ||
        res?.data?.detail ||
        "Invoice deleted successfully";
      SuccessNotificationMsg(msg);
      fetchDraftGoodsReceipts();
    } catch (err) {
      ShowErrorMsg(err);
    }
  };

  const columns = [
    {
      title: "Source Document",
      dataIndex: "sourcedocument",
      key: "sourcedocument",
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
      width: 200,
    },
    {
      title: "Site",
      dataIndex: "site",
      key: "site",
      width: 150,
    },
    {
      title: "Extracted Date",
      dataIndex: "extracted_date",
      key: "extracted_date",
      width: 200,
    },
    {
      title: "AI Confidence",
      dataIndex: "confidence",
      key: "confidence",
      width: 250,
      render: (value) => (
        <Progress
          percent={value}
          size="small"
          status={value < 40 ? "exception" : ""}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 340,
      render: (value) => <span className="gridstatus">{value || "Draft"}</span>,
    },
    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 180,
      render: (text, record) => (
        <Space>
          {record.document_file_url ? (
            <Tooltip title="View document">
              <span
                role="button"
                tabIndex={0}
                onClick={() => window.open(record.document_file_url, "_blank", "noopener,noreferrer")}
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  window.open(record.document_file_url, "_blank", "noopener,noreferrer")
                }
                style={{ cursor: "pointer" }}
              >
                <EyeOutlined />
              </span>
            </Tooltip>
          ) : null}
          {embedded && onEditDraft ? (
            <span
              role="button"
              tabIndex={0}
              onClick={() => onEditDraft(record.id)}
              onKeyDown={(e) => e.key === "Enter" && onEditDraft(record.id)}
              style={{ cursor: "pointer" }}
            >
              <EditOutlined />
            </span>
          ) : (
            <Link to={`/edit-inventory-ai-doc-intake/${record.id}`}>
              <EditOutlined />
            </Link>
          )}
          <Popconfirm
            title="Delete this draft?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDelete(record)}
          >
            <Link to="#" onClick={(e) => e.preventDefault()}>
              <DeleteOutlined />
            </Link>
          </Popconfirm>
        </Space>
      ),
    },
  ];
  return (
    <>
      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body">
          <div className="tablewrp">
            <Table
              scroll={{
                x: 1000,
              }}
              dataSource={tableData}
              columns={columns}
              loading={loading}
              rowKey={(record) => record.id ?? record.key}
              pagination={{
                current: state.page,
                pageSize: state.page_size,
                total: totalCount,
                showSizeChanger: true,
                locale: {
                  items_per_page: "Items / Page",
                },
                showTotal: (total) => `Total ${total} items`,
              }}
              onChange={handleTableChange}
            />
          </div>
        </div>
      </section>

      <Modal
        open={modalOpen}
        title="Confirm Physical Count"
        className="ModelPopupSec"
        wrapClassName="modal-wrapper"
        okText="Start Count"
        cancelText="Cancel"
        onCancel={() => setModalOpen(false)}
        onOk={() => {
          message.success("Physical count updated successfully");
          message.info("Table update functionality not yet implemented");

          setModalOpen(false);
        }}
      >
        <p>
          Are you sure you want to start the physical count for this goods
          receipt?
        </p>
        <div className="good-modal-footer">
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>

          <Button
            type="primary"
            danger={false}
            onClick={() => {
              message.success("Physical count updated successfully");
              setModalOpen(false);
              navigate("/confirm-goods-receipt");
            }}
          >
            Confirm
          </Button>
        </div>
      </Modal>
    </>
  );
};

DraftGoodsReceipt.propTypes = {
  embedded: PropTypes.bool,
  refreshKey: PropTypes.number,
  onAddReceipt: PropTypes.func,
  onEditDraft: PropTypes.func,
};

export default DraftGoodsReceipt;
